import { CompassOutlined, MailOutlined, PlayCircleOutlined, ReadOutlined } from '@ant-design/icons';
import { Button, Divider, Drawer, Flex, Tour, Typography } from 'antd';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePublicContact } from '../api/hooks';
import { useSiteContext } from '../context/SiteContext';
import {
  TOURS,
  anchorElement,
  helpTopicFor,
  tourFor,
  type Tour as TourDef,
  type TourId,
} from './tours';

interface HelpState {
  /** the guided tour of the current page, if it has one */
  pageTour?: TourId;
  startTour: (id: TourId) => void;
  openHelp: () => void;
}

const HelpContext = createContext<HelpState | null>(null);

/** The help center of the portal, or null outside it (the vendor console). */
export function useHelp(): HelpState | null {
  return useContext(HelpContext);
}

const storageKey = (userId: string) => `wm.tours.${userId}`;

function seenTours(userId: string): TourId[] {
  try {
    return JSON.parse(localStorage.getItem(storageKey(userId)) ?? '[]') as TourId[];
  } catch {
    return [];
  }
}

function markSeen(userId: string, id: TourId) {
  try {
    const seen = new Set(seenTours(userId)).add(id);
    localStorage.setItem(storageKey(userId), JSON.stringify([...seen]));
  } catch {
    // storage refused: the tour may start again on the next visit
  }
}

/**
 * Guided tours and contextual help (FR-TRV-30). The tour of the portal starts once by itself on
 * the dashboard of a person's first visit (remembered per browser); every tour, the help of the
 * page and the support address stay in the help menu of every screen.
 */
export function HelpProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { me } = useSiteContext();
  const userId = me?.principal.userId ?? undefined;
  const [tour, setTour] = useState<TourDef | null>(null);
  const [current, setCurrent] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  const startTour = useCallback(
    (id: TourId) => {
      const definition = TOURS.find((x) => x.id === id);
      if (!definition) return;
      setHelpOpen(false);
      const begin = () => {
        setCurrent(0);
        setTour(definition);
      };
      if (location.pathname === definition.path) {
        begin();
      } else {
        navigate(definition.path);
        // the page renders its anchors first
        setTimeout(begin, 900);
      }
    },
    [location.pathname, navigate],
  );

  // a person's first visit: the tour of the portal, once
  useEffect(() => {
    if (!userId || location.pathname !== '/' || seenTours(userId).includes('discover')) return;
    const timer = setTimeout(() => {
      setCurrent(0);
      setTour(TOURS.find((x) => x.id === 'discover') ?? null);
    }, 1200);
    return () => clearTimeout(timer);
  }, [userId, location.pathname]);

  const close = useCallback(() => {
    if (tour && userId) markSeen(userId, tour.id);
    setTour(null);
  }, [tour, userId]);

  const steps = useMemo(
    () =>
      (tour?.steps ?? []).map((s) => ({
        title: t(`tours.${tour?.id}.${s.key}.title`),
        description: t(`tours.${tour?.id}.${s.key}.text`),
        target: () => anchorElement(s.anchor) as HTMLElement,
        ...(s.beside && {
          placement:
            document.documentElement.dir === 'rtl' ? ('left' as const) : ('right' as const),
        }),
      })),
    [tour, t],
  );

  const value = useMemo<HelpState>(
    () => ({
      pageTour: tourFor(location.pathname)?.id,
      startTour,
      openHelp: () => setHelpOpen(true),
    }),
    [location.pathname, startTour],
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      <Tour
        open={Boolean(tour)}
        steps={steps}
        current={current}
        onChange={setCurrent}
        onClose={close}
        onFinish={close}
        zIndex={1100}
      />
      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        topic={helpTopicFor(location.pathname)}
        pageTour={value.pageTour}
        onTour={startTour}
      />
    </HelpContext.Provider>
  );
}

function HelpDrawer(props: {
  open: boolean;
  onClose: () => void;
  topic: string;
  pageTour?: TourId;
  onTour: (id: TourId) => void;
}) {
  const { t } = useTranslation();
  const contact = usePublicContact();
  const support = contact.data?.support;
  const base = `help.topics.${props.topic}`;
  const list = (key: string) => {
    const value = t(`${base}.${key}`, { returnObjects: true, defaultValue: [] });
    return Array.isArray(value) ? (value as string[]) : [];
  };
  const steps = list('steps');
  const tips = list('tips');
  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      size={440}
      title={
        <Flex align="center" gap={8}>
          <ReadOutlined />
          {t('help.panelTitle')}
        </Flex>
      }
    >
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {t(`${base}.title`)}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t(`${base}.summary`)}</Typography.Paragraph>
      {steps.length > 0 && (
        <>
          <div className="wm-section-title">{t('help.steps')}</div>
          <ol style={{ paddingInlineStart: 20, margin: '0 0 16px' }}>
            {steps.map((s) => (
              <li key={s} style={{ marginBottom: 6 }}>
                {s}
              </li>
            ))}
          </ol>
        </>
      )}
      {tips.length > 0 && (
        <>
          <div className="wm-section-title">{t('help.tips')}</div>
          <ul style={{ paddingInlineStart: 20, margin: '0 0 16px' }}>
            {tips.map((s) => (
              <li key={s} style={{ marginBottom: 6 }}>
                {s}
              </li>
            ))}
          </ul>
        </>
      )}
      <Flex vertical gap={8} align="flex-start" style={{ marginTop: 8 }}>
        {props.pageTour && (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => props.onTour(props.pageTour!)}
          >
            {t('help.tourOfPage')}
          </Button>
        )}
        <Button icon={<CompassOutlined />} onClick={() => props.onTour('discover')}>
          {t('help.tourOfPortal')}
        </Button>
      </Flex>
      {support && (
        <>
          <Divider />
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {t('help.stillStuck')}
          </Typography.Paragraph>
          <Button icon={<MailOutlined />} href={`mailto:${support}`}>
            {support}
          </Button>
        </>
      )}
    </Drawer>
  );
}
